/**
 * Session Hook - Backend-based authentication state
 *
 * This hook is the ONLY source of truth for authentication state in the app.
 * It fetches session data from the server which manages HttpOnly cookie sessions.
 *
 * DO NOT use WorkOS useAuth() for gating - only use this hook.
 */

import {
  useState,
  useEffect,
  useCallback,
  createContext,
  useContext,
  type ReactNode,
} from "react";

export interface SessionUser {
  id: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

export interface Session {
  user: SessionUser | null;
  expiresAt: number | null;
}

export interface SessionContextValue {
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  user: SessionUser | null;
  login: () => void;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

/**
 * Fetch current session from backend
 */
async function fetchSession(): Promise<Session> {
  try {
    const res = await fetch("/api/auth/session", {
      credentials: "include",
      headers: {
        Accept: "application/json",
      },
    });

    if (!res.ok) {
      console.error("[useSession] Session fetch failed:", res.status);
      return { user: null, expiresAt: null };
    }

    const data = await res.json();
    return {
      user: data.user || null,
      expiresAt: data.expiresAt || null,
    };
  } catch (error) {
    console.error("[useSession] Session fetch error:", error);
    return { user: null, expiresAt: null };
  }
}

/**
 * Session Provider Component
 * Wrap your app with this to provide session context
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initial session fetch
  useEffect(() => {
    let mounted = true;

    async function loadSession() {
      const sessionData = await fetchSession();
      if (mounted) {
        setSession(sessionData);
        setIsLoading(false);
      }
    }

    loadSession();

    return () => {
      mounted = false;
    };
  }, []);

  // Login - redirect to /api/auth/start
  // Always redirects to / after auth - client-side will then redirect to /#app
  const login = useCallback(() => {
    window.location.href = `/api/auth/start?returnTo=/`;
  }, []);

  // Logout - call API and clear session
  const logout = useCallback(async () => {
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      console.error("[useSession] Logout error:", error);
    }

    setSession({ user: null, expiresAt: null });

    // Redirect to home
    window.location.href = "/";
  }, []);

  // Refresh session (e.g., after token refresh)
  const refreshSession = useCallback(async () => {
    const sessionData = await fetchSession();
    setSession(sessionData);
  }, []);

  const value: SessionContextValue = {
    session,
    isLoading,
    isAuthenticated: !!session?.user,
    user: session?.user || null,
    login,
    logout,
    refreshSession,
  };

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

/**
 * Hook to access session state
 * Must be used within SessionProvider
 */
export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);

  if (!context) {
    throw new Error("useSession must be used within a SessionProvider");
  }

  return context;
}

/**
 * Fetch access token for Convex
 * Called by ConvexProviderWithAuth
 */
export async function fetchAccessToken(): Promise<string | null> {
  try {
    const res = await fetch("/api/auth/token", {
      credentials: "include",
      headers: {
        Accept: "application/json",
      },
    });

    if (!res.ok) {
      // 401 means not authenticated - this is normal
      if (res.status === 401) {
        return null;
      }
      console.error("[fetchAccessToken] Token fetch failed:", res.status);
      return null;
    }

    const data = await res.json();
    return data.accessToken || null;
  } catch (error) {
    console.error("[fetchAccessToken] Token fetch error:", error);
    return null;
  }
}
